import Render from "components/Common/Render";
import { useLocalStorage } from "hooks/useLocalStorage";
import { useAppDispatch, useAppSelector } from "hooks/useRedux";
import { useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { RootState } from "store";
import { useLazyBusinessListQuery } from "store/apis/businessListApi";
import { setBusiness, setBusinesses } from "store/slices/businessSlice";
import { ReactComponent as EmptyLogo } from "assets/empty_business_logo_square.svg";
import { Loader } from "components/Common/Loader";
import { checkIsAnywhereCommunity } from "utils/common";
import SelectionList from "components/Common/SelectionList";
import { BusinessStatus } from "enum/common";
import Badge from "components/Common/Badge";
import useMixpanel from "hooks/useMixpanel";
import { useLazyDefaultAccountQuery } from "store/apis/defaultAccountApi";
import { setDefaultAccount } from "store/slices/defaultAccountSlice";
interface IChooseBusinessType {
  variant?: "popover" | "fullscreen";
  showPopover?: boolean;
  setShowPopover?: (value: boolean) => void;
  onRadioSelectionChange?: (uuid: string) => void;
}

const ChooseBusiness: React.FC<IChooseBusinessType> = ({
  variant = "fullscreen",
  setShowPopover,
}) => {
  const { businessId } = useParams();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { trackEvent } = useMixpanel();

  const [fetchBusinessses, { data, isFetching, isSuccess }] =
    useLazyBusinessListQuery();
  const businessList = useAppSelector(
    (state: RootState) => state?.businesses?.businesses
  );
  const [
    fetchDefaultAccount,
    {
      data: defaultPaymentMethod,
      isLoading: isFetchingPaymentMethod,
      isSuccess: isAccountsetupSuccess,
    },
  ] = useLazyDefaultAccountQuery({});

  const [selectedBusiness, setSelectedBusiness] = useLocalStorage(
    "selectedBusiness",
    null
  );

  const renderBusinessDetails = (business: EntityType) => {
    const isInactive =
      business?.business?.businessStatus === BusinessStatus.IN_ACTIVE;
    return (
      <div className={`px-5 flex flex-row `}>
        <div>
          {business?.business?.logo ? (
            <img
              src={business?.business?.logo}
              className="inline-block h-12 w-12 rounded-full ring-2 ring-white"
            />
          ) : (
            <EmptyLogo className="inline-block h-12 w-12 rounded-full ring-2 ring-white" />
          )}
        </div>
        <div className="ml-3 justify-center flex flex-col">
          <div className="text-darkBlue text-lg">
            {business?.business?.name}
            {isInactive && (
              <Badge label="Pending Approval" customClass="ml-2" />
            )}
          </div>
          {!checkIsAnywhereCommunity(business?.business) && (
            <div className="text-lightGrey">
              {business?.business?.communityName}{" "}
            </div>
          )}
        </div>
      </div>
    );
  };

  const businessOptions = useMemo(
    () =>
      businessList?.map((business: EntityType) => ({
        label: renderBusinessDetails(business),
        value: business?.business?.uuid || "",
        data: business,
      })) || [],
    [businessList]
  );

  const handleBusinessChange = (
    uuid: string,
    myBusinesses: EntityType[] = businessList || []
  ) => {
    trackEvent("select_business_from_list", { businessId: uuid });
    const filteredBusiness = myBusinesses?.filter(
      (business: EntityType) => business?.business?.uuid === uuid
    )[0];
    fetchDefaultAccount({ uuid });
    if (filteredBusiness) {
      setSelectedBusiness({ ...filteredBusiness?.business });
      dispatch(setBusiness({ ...filteredBusiness?.business }));
      if (variant === "popover") {
        setShowPopover && setShowPopover(false);
      } else {
        navigate("/", { state: { isBusinessRefreshed: true } });
      }
    }
  };

  useEffect(() => {
    if (businessId !== "1" && !isFetching) {
      handleBusinessChange(businessId || "");
    }
  }, [isFetching, businessId, businessList]);

  useEffect(() => {
    fetchBusinessses();
  }, []);

  useEffect(() => {
    if (isSuccess && data) {
      dispatch(setBusinesses(data || []));
    }
    if (data && data?.length === 1) {
      handleBusinessChange(data[0]?.business?.uuid || "", data);
    }
  }, [data]);

  useEffect(() => {
    if (isAccountsetupSuccess && defaultPaymentMethod) {
      dispatch(setDefaultAccount(defaultPaymentMethod));
    }
  }, [defaultPaymentMethod, isAccountsetupSuccess]);

  const containerStyle =
    variant === "fullscreen"
      ? "bg-offWhite  p-4 mx-auto md:w-3/5  h-screen overflow-hidden justify-center items-center overflow-hidden"
      : "h-full overflow-hidden";

  return (
    <div className={containerStyle}>
      <div className="flex flex-col gap-4">
        <Render when={variant === "fullscreen"}>
          <h2 className="font-medium text-3xl text-darkBlue">
            Choose Business
          </h2>
          <p className="text-black60 font-regular text-sm">
            We found the following business profiles that are matched to your
            email in our system
          </p>
        </Render>
        <div
          className={`bg-white p-4 h-[80vh] no-scrollbar ${
            isFetching ? "overflow-hidden" : "overflow-scroll"
          }`}
        >
          <Render when={!isFetching && !businessOptions?.length}>
            <div className="justify-center flex items-center h-full">
              No Businesses Found
            </div>
          </Render>
          {isFetching ? (
            <div className="flex justify-center items-center h-full">
              <Loader size={8} color={"darkBlue"} />
            </div>
          ) : (
            <SelectionList
              onChange={(option) => {
                handleBusinessChange(option.value);
              }}
              options={businessOptions}
              containerClass={"text-sm"}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default ChooseBusiness;
